import { Box } from '@mui/material';
import { GridContainer, GridItem } from 'components/atomic/atoms/grid';
import Image from 'next/image';
import type { ReactNode } from 'react';
import { effects } from 'design-system/tokens/effects';
import { bentoTokens } from '../bento-tokens';
import { renderSeeAllButton } from './shared-helpers';

interface GridStateProps {
  images: string[];
  getAlt: (index: number) => string;
  onImageClick?: (index: number) => void;
  blur?: boolean;
  badgesSlot?: ReactNode;
  seeAllSlot?: (total: number) => ReactNode;
  mapSlot?: ReactNode;
  onSeeAllClick?: () => void;
  total: number;
}

/**
 * Bento Grid State (N ≥ 6 Images)
 * 
 * Layout: 8-2-2 grid (main image + 2 columns of 3 thumbnails)
 * - Slot 1: Main Hero Image
 * - Slots 2-7: Thumbnails (2 columns x 3 rows)
 * - N=6: Slots 1-6 filled, slots 6 & 7 combined for map
 * - N=7: Slots 1-7 filled, slot 7 shows map
 * - N≥8: All slots filled with images
 */
export function BentoGridState({
  images,
  getAlt,
  onImageClick,
  blur,
  badgesSlot,
  seeAllSlot,
  mapSlot,
  total,
}: GridStateProps) {
  const mainImage = images[0];
  const remainingImages = images.slice(1);
  const maxThumbnails = 6;

  const displayImages = remainingImages.slice(0, maxThumbnails);

  // Split thumbnails into 2 columns (3 per column)
  const column1Thumbnails = displayImages.slice(0, 3);
  const column2Thumbnails = displayImages.slice(3, 6);

  const showColumn1 = column1Thumbnails.length > 0;
  const showColumn2 = column2Thumbnails.length > 0;

  // Determine where to show map based on image count
  const showMapInColumn1 = images.length === 6 && mapSlot; // N=6: combine bottom 2 slots
  const showMapInColumn2 = images.length === 7 && mapSlot; // N=7: last slot

  return (
    <GridContainer
      data-testid="gallery-bento-grid"
      columnSpacing={bentoTokens.gap}
      sx={{
        filter: blur ? effects.blur[100] : 'none',
      }}
    >
      {/* Main Image (8/12 columns) */}
      <GridItem xs={bentoTokens.gridColumns.mainImage}>
        <Box
          onClick={() => onImageClick?.(0)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && onImageClick?.(0)}
          sx={{
            position: 'relative',
            width: '100%',
            height: bentoTokens.height,
            borderRadius: bentoTokens.borderRadius.main,
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          <Image
            src={mainImage}
            alt={getAlt(0)}
            fill
            sizes={bentoTokens.imageSizes.mainImage}
            style={{ objectFit: 'cover' }}
            priority
          />
          {badgesSlot && (
            <Box
              sx={{
                position: 'absolute',
                top: bentoTokens.overlay.badges,
                left: bentoTokens.overlay.badges,
                zIndex: bentoTokens.zIndex.badges,
              }}
            >
              {badgesSlot}
            </Box>
          )}
        </Box>
      </GridItem>

      {/* Column 1 (2/12 columns) */}
      {showColumn1 && (
        <GridItem xs={bentoTokens.gridColumns.thumbnail}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: bentoTokens.gap,
              height: bentoTokens.height,
            }}
          >
            {column1Thumbnails.map((url, index) => {
              const imageIndex = index + 1;
              const isLast = index === column1Thumbnails.length - 1;
              const isLastOverall = !showColumn2 && isLast;

              return (
                <Box
                  key={imageIndex}
                  onClick={() => onImageClick?.(imageIndex)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && onImageClick?.(imageIndex)}
                  sx={{
                    position: 'relative',
                    flex: 1,
                    borderRadius: bentoTokens.borderRadius.thumbnail,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.95,
                    },
                  }}
                >
                  <Image
                    src={url}
                    alt={getAlt(imageIndex)}
                    fill
                  sizes={bentoTokens.imageSizes.thumbnail}
                  style={{ objectFit: 'cover' }}
                />
                {isLastOverall && renderSeeAllButton(seeAllSlot, total)}
              </Box>
              );
            })}

            {/* Map for N=6: combine bottom 2 slots */}
            {showMapInColumn1 && column1Thumbnails.length === 2 && (
              <Box
                sx={{
                  flex: 1,
                  borderRadius: bentoTokens.borderRadius.thumbnail,
                  overflow: 'hidden',
                }}
              >
                {mapSlot}
              </Box>
            )}
          </Box>
        </GridItem>
      )}

      {/* Column 2 (2/12 columns) */}
      {showColumn2 && (
        <GridItem xs={bentoTokens.gridColumns.thumbnail}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: bentoTokens.gap,
              height: bentoTokens.height,
            }}
          >
            {column2Thumbnails.map((url, index) => {
              const imageIndex = index + 1 + column1Thumbnails.length;
              const isLast = index === column2Thumbnails.length - 1;

              return (
                <Box
                  key={imageIndex}
                  onClick={() => onImageClick?.(imageIndex)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && onImageClick?.(imageIndex)}
                  sx={{
                    position: 'relative',
                    flex: 1,
                    borderRadius: bentoTokens.borderRadius.thumbnail,
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                >
                  <Image
                    src={url}
                    alt={getAlt(imageIndex)}
                    fill
                  sizes={bentoTokens.imageSizes.thumbnail}
                  style={{ objectFit: 'cover' }}
                />
                {isLast && renderSeeAllButton(seeAllSlot, total)}
              </Box>
              );
            })}

            {/* Map for N=7: last slot in column 2 */}
            {showMapInColumn2 && column2Thumbnails.length === 2 && (
              <Box
                sx={{
                  flex: 1,
                  borderRadius: bentoTokens.borderRadius.thumbnail,
                  overflow: 'hidden',
                }}
              >
                {mapSlot}
              </Box>
            )}
          </Box>
        </GridItem>
      )}
    </GridContainer>
  );
}
